import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, AlertCircle, PenLine, CheckCircle2, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/config";

const SignaturePage: React.FC = () => {
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [errorJson, setErrorJson] = useState<any>(null);
    const [signaturePolling, setSignaturePolling] = useState(false);
    const pollingInFlight = useRef(false);

    const [searchParams] = useSearchParams();
    const classId = searchParams.get("c");
    const emailParam = searchParams.get("e");

    // Auto-submit if email provided in URL
    useEffect(() => {
        if (emailParam && classId) {
            setEmail(emailParam);
            handleSubmit(emailParam);
        }
    }, []);

    // Poll for signature completion
    useEffect(() => {
        if (!signaturePolling || !email || !classId) return;

        const pollInterval = setInterval(async () => {
            if (pollingInFlight.current) return;
            pollingInFlight.current = true;
            try {
                const response = await fetch(`${API_URL}/check-class-signing`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Accept": "application/json" },
                    body: JSON.stringify({ email, class_id: classId, polling: true }),
                });
                const data = await response.json();
                if (response.ok && data.already_signed) {
                    setResult(data);
                    setSignaturePolling(false);
                    toast.success("Signature confirmée !");
                }
            } catch {
                // Ignore polling errors
            } finally {
                pollingInFlight.current = false;
            }
        }, 10000);

        return () => clearInterval(pollInterval);
    }, [signaturePolling, email, classId]);

    const handleSubmit = async (emailToSubmit?: string) => {
        const submitEmail = emailToSubmit || email;
        if (!submitEmail.trim() || !classId) return;

        setIsSubmitting(true);
        setErrorJson(null);
        setResult(null);

        try {
            const response = await fetch(`${API_URL}/check-class-signing`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Accept": "application/json" },
                body: JSON.stringify({ email: submitEmail, class_id: classId }),
            });

            const data = await response.json();

            if (response.ok) {
                setResult(data);
                if (data.signature_required) {
                    setSignaturePolling(true);
                }
            } else {
                setErrorJson(data);
            }
        } catch (error: any) {
            setErrorJson({ error: "api_error", message: error.message || "Une erreur est survenue." });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Verify signature without destroying the iframe
    const handleVerify = async () => {
        if (!email.trim() || !classId) return;
        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/check-class-signing`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Accept": "application/json" },
                body: JSON.stringify({ email, class_id: classId, polling: true }),
            });
            const data = await response.json();
            if (response.ok && data.already_signed) {
                setResult(data);
                setSignaturePolling(false);
                toast.success("Signature confirmée !");
            } else {
                toast.info("Signature non détectée. Veuillez signer dans le cadre ci-contre puis réessayer.");
            }
        } catch {
            toast.error("Erreur de vérification. Réessayez.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // No class ID in URL
    if (!classId) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <Card className="w-full max-w-md border-slate-200 shadow-xl bg-white/80 backdrop-blur-sm text-center p-8">
                    <div className="h-16 w-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="h-8 w-8" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Lien incorrect</h1>
                    <p className="text-slate-500 mb-8">Ce lien de signature est invalide ou incomplet.</p>
                </Card>
            </div>
        );
    }

    // Already signed
    if (result?.already_signed) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <div className="text-center space-y-6 max-w-md animate-in fade-in zoom-in duration-500">
                    <div className="relative mx-auto w-20 h-20">
                        <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
                        <div className="relative h-20 w-20 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-2xl shadow-emerald-500/40">
                            <CheckCircle2 className="h-10 w-10" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-2xl font-black text-slate-900">Signature confirmée</h1>
                        <p className="text-slate-500 font-medium">
                            Vous avez déjà signé la fiche de présence pour cette classe. Merci !
                        </p>
                    </div>
                    <p className="text-center text-xs font-semibold text-slate-400 uppercase tracking-widest mt-8">
                        AMC Lead Dispatch V2.1
                    </p>
                </div>
            </div>
        );
    }

    // Edusign not synced yet
    if (result?.signing_unavailable) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <Card className="w-full max-w-md border-slate-200 shadow-xl bg-white/80 backdrop-blur-sm text-center p-8">
                    <div className="h-16 w-16 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Info className="h-8 w-8" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Signature non disponible</h1>
                    <p className="text-slate-500 mb-6">
                        La fiche de présence n'est pas encore disponible. Veuillez réessayer dans quelques minutes.
                    </p>
                    <Button onClick={() => handleSubmit()} className="w-full font-bold">
                        Réessayer
                    </Button>
                </Card>
            </div>
        );
    }

    // Signature required — show iframe
    if (result?.signature_required) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                    <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-50" />
                    <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-blue-500/5 rounded-full blur-3xl opacity-50" />
                </div>

                <div className="flex-1 z-10 flex flex-col lg:flex-row w-full animate-in fade-in duration-500">
                    {/* Left column: Info */}
                    <div className="lg:w-[380px] xl:w-[420px] shrink-0 flex flex-col justify-center items-center p-6 lg:p-10">
                        <div className="mb-8 text-center">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary text-primary-foreground mb-4 shadow-lg shadow-primary/20">
                                <PenLine className="h-6 w-6" />
                            </div>
                            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 mb-2">
                                Fiche de Présence
                            </h1>
                            <p className="text-slate-500 font-medium text-sm">
                                Signez la fiche de présence pour valider votre participation.
                            </p>
                        </div>

                        <div className="space-y-4 w-full">
                            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-sm">
                                <p className="font-bold mb-1 flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                    Présence enregistrée
                                </p>
                                <p>Votre présence a été notée. Veuillez également signer la fiche ci-contre.</p>
                            </div>

                            {result.signature_link ? (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                                    <p className="font-bold mb-1 flex items-center gap-2">
                                        <PenLine className="h-4 w-4 text-amber-600" />
                                        Signature requise
                                    </p>
                                    <p>Veuillez signer la fiche de présence ci-contre.</p>
                                </div>
                            ) : (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                                    <p className="font-bold mb-1 flex items-center gap-2">
                                        <PenLine className="h-4 w-4 text-amber-600" />
                                        Signature par email
                                    </p>
                                    <p>Un email de signature vous sera envoyé par <span className="font-bold">EduSign</span>.</p>
                                </div>
                            )}

                            <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg">
                                <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                                <p className="text-xs text-slate-500 font-medium">
                                    Vérification automatique en cours...
                                </p>
                            </div>

                            <Button
                                onClick={handleVerify}
                                disabled={isSubmitting}
                                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold"
                            >
                                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenLine className="h-4 w-4 mr-2" />}
                                J'ai signé, vérifier
                            </Button>
                        </div>

                        <p className="mt-8 text-center text-xs font-semibold text-slate-400 uppercase tracking-widest">
                            AMC Lead Dispatch V2.1
                        </p>
                    </div>

                    {/* Right column: Signature iframe */}
                    {result.signature_link && (
                        <div className="flex-1 p-4 lg:p-6 flex flex-col min-h-[600px] lg:min-h-0">
                            <div className="relative flex-1 w-full rounded-xl overflow-hidden border border-slate-200 bg-white shadow-lg">
                                <iframe
                                    src={result.signature_link}
                                    className="absolute inset-0 w-full h-full"
                                    style={{ border: "none" }}
                                    title="EduSign - Signature de présence"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Error states
    if (errorJson) {
        const errorConfig: Record<string, { icon: React.ReactNode; title: string; message: string; iconClass: string }> = {
            signing_closed: {
                icon: <Clock className="h-8 w-8" />,
                title: "Signature terminée",
                message: errorJson.message || "La période de signature est terminée.",
                iconClass: "bg-amber-50 text-amber-500",
            },
            not_permitted: {
                icon: <AlertCircle className="h-8 w-8" />,
                title: "Accès refusé",
                message: errorJson.message || "Vous n'êtes pas inscrit à cette classe.",
                iconClass: "bg-red-50 text-red-500",
            },
        };

        const config = errorConfig[errorJson.error] || {
            icon: <AlertCircle className="h-8 w-8" />,
            title: "Erreur",
            message: errorJson.message || "Une erreur est survenue.",
            iconClass: "bg-red-50 text-red-500",
        };

        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <Card className="w-full max-w-md border-slate-200 shadow-xl bg-white/80 backdrop-blur-sm text-center p-8">
                    <div className={`h-16 w-16 ${config.iconClass} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                        {config.icon}
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">{config.title}</h1>
                    <p className="text-slate-500 mb-6">{config.message}</p>
                    <Button
                        onClick={() => { setErrorJson(null); setResult(null); }}
                        variant="outline"
                        className="w-full font-bold"
                    >
                        Réessayer
                    </Button>
                </Card>
            </div>
        );
    }

    // Default: Email form
    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-50" />
                <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-blue-500/5 rounded-full blur-3xl opacity-50" />
            </div>

            <div className="z-10 w-full max-w-md space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground mb-6 shadow-2xl shadow-primary/30">
                        <PenLine className="h-8 w-8" />
                    </div>
                    <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-2">
                        Fiche de Présence
                    </h1>
                    <p className="text-slate-500 font-medium">
                        Entrez votre email pour signer la fiche de présence.
                    </p>
                </div>

                <form
                    onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
                    className="space-y-4"
                >
                    <div className="space-y-2">
                        <Input
                            type="email"
                            placeholder="votre@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="h-14 text-center text-lg font-medium bg-white border-slate-200 rounded-2xl shadow-sm focus:ring-primary/20 placeholder:text-slate-300"
                            required
                            autoFocus
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={isSubmitting || !email.trim()}
                        className="w-full h-14 text-lg font-bold shadow-xl shadow-primary/20 rounded-2xl"
                    >
                        {isSubmitting ? (
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        ) : (
                            <PenLine className="h-5 w-5 mr-2" />
                        )}
                        Signer la fiche de présence
                    </Button>
                </form>

                <p className="text-center text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    AMC Lead Dispatch V2.1
                </p>
            </div>
        </div>
    );
};

export default SignaturePage;
